import { useDisclosure } from "@/hooks";
import {
	useChangeMeditatorRoom,
	useGetRoomsByMeditationEvent,
	useGetWaitingMeditators,
} from "@/hooks/query-hooks/useRoom";
import { CloseOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Drawer, Empty, Modal, Radio, Space } from "antd";
import { useMemo, useState } from "react";
import { RoomCard } from "./RoomCard";
import { UserCard } from "./UserCard";

export function WaitingRoom() {
	const [open, { toggle }] = useDisclosure(false);

	const { data: waitingMeditators = [], isLoading } =
		useGetWaitingMeditators(open);

	return (
		<>
			<Button
				key={"waiting-room"}
				size="large"
				className="text-primary-gradient border-f42! rounded-lg!"
				onClick={toggle}
				icon={<TeamOutlined className="text-f42!" />}
			>
				Waiting Room
			</Button>
			<Drawer
				title={
					<article className="flex items-center justify-between">
						<h2 className="text-xl font-semibold text-primary-gradient font-poppins">
							Waiting Room
						</h2>
						<Button
							onClick={toggle}
							shape="circle"
							size="large"
							type="text"
							icon={<CloseOutlined />}
							className="bg-gray-100! hover:bg-gray-200! text-base!"
						/>
					</article>
				}
				width={600}
				open={open}
				onClose={toggle}
				closeIcon={null}
				loading={isLoading}
			>
				{waitingMeditators?.length > 0 ? (
					<Space
						direction="vertical"
						className="w-full"
						size={"middle"}
					>
						{waitingMeditators?.map((it, idx) => (
							<UserCard
								key={it.id}
								user={it}
								index={idx + 1}
								extra={
									<Space>
										<ChangeRoomAction {...it} />
									</Space>
								}
							/>
						))}
					</Space>
				) : (
					<Empty
						image={<TeamOutlined className="text-8xl" />}
						description="No waiting users found!"
						classNames={{
							root: "mt-8",
							description: "font-poppins!",
						}}
					/>
				)}
			</Drawer>
		</>
	);
}

const ChangeRoomAction = (props: RoomRequestUser) => {
	const { eventId, userId, userName } = props;
	const [selectedRoomId, setSelectedRoomId] = useState<null | number>(null);
	const [isRoomsAvailable, setIsRoomsAvailable] = useState(true);

	const [open, { toggle }] = useDisclosure();
	const [modal, contextHolder] = Modal.useModal();

	const { data: rooms = [], isLoading } = useGetRoomsByMeditationEvent(
		eventId,
		open,
	);

	const filteredRooms = useMemo(() => {
		switch (isRoomsAvailable) {
			case true:
				return rooms?.filter((it) => it.count !== it.maxiCount);
			case false:
				return rooms?.filter((it) => it.count === it.maxiCount);
		}
	}, [rooms, isRoomsAvailable]);

	const { mutate, isPending } = useChangeMeditatorRoom();

	const handleRoomChange = (room: Room) => {
		modal.confirm({
			title: (
				<h2 className="font-poppins text-primary-gradient">
					Add {userName} to {room.name}?
				</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to move {userName} to {room.name}?
				</p>
			),
			okText: "Confirm",
			okButtonProps: {
				loading: isPending,
				type: "primary",
				className: "bg-primary-gradient rounded-lg!",
			},
			cancelButtonProps: {
				type: "text",
				disabled: isPending,
			},
			onOk: () => {
				return new Promise((resolve, reject) => {
					mutate(
						{ roomId: room.id, userId },
						{
							onSuccess: resolve,
							onError: reject,
						},
					);
				});
			},
			onCancel: () => setSelectedRoomId(null),
		});
	};

	return (
		<>
			<Button
				icon={
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="14"
						height="14"
						viewBox="0 0 14 14"
						fill="none"
					>
						<path
							d="M7 1V13M1 7H13"
							stroke="url(#paint0_linear_60_3893)"
							strokeWidth="2"
							strokeLinecap="round"
						/>
						<defs>
							<linearGradient
								id="paint0_linear_60_3893"
								x1="7"
								y1="1"
								x2="7"
								y2="13"
								gradientUnits="userSpaceOnUse"
							>
								<stop stopColor="#F88A0C" />
								<stop offset="1" stopColor="#FF4D21" />
							</linearGradient>
						</defs>
					</svg>
				}
				className=" bg-gradient-to-b! from-f80/20 to-f42/20 border border-f80/50!"
				shape="circle"
				onClick={toggle}
				type="text"
			/>
			<Modal
				open={open}
				onCancel={() => {
					toggle();
					setSelectedRoomId(null);
				}}
				title={
					<h2 className="font-poppins text-primary-gradient">
						Select Room for {userName}
					</h2>
				}
				footer={null}
				styles={{
					body: {
						paddingTop: 8,
						height: 640,
						overflow: "scroll",
					},
				}}
				loading={isLoading}
				width={780}
				height={640}
			>
				{contextHolder}

				{rooms?.length > 0 ? (
					<>
						<Card
							title={
								<Radio.Group
									options={[
										{
											label: "Available",
											value: true,
											style: {
												fontFamily:
													"var(--font-poppins)",
											},
										},
										{
											label: "Unavailable",
											value: false,
											style: {
												fontFamily:
													"var(--font-poppins)",
											},
										},
									]}
									value={isRoomsAvailable}
									onChange={(e) =>
										setIsRoomsAvailable(e.target.value)
									}
								/>
							}
							// extra={[
							// 	<div
							// 		className="flex items-center gap-2 justify-end"
							// 		key={"status"}
							// 	>
							// 		{[
							// 			{
							// 				bg_color: "bg-white",
							// 				label: "Available",
							// 			},
							// 			{
							// 				bg_color: "bg-primary-gradient",
							// 				label: "Selected",
							// 			},
							// 			{
							// 				bg_color: "bg-gray-200",
							// 				label: "Full",
							// 			},
							// 		].map(({ bg_color, label }, idx) => (
							// 			<Space key={idx}>
							// 				<div
							// 					className={cn(
							// 						"size-4 border border-gray-300 rounded-sm",
							// 						bg_color,
							// 					)}
							// 				/>
							// 				<p className="font-poppins">
							// 					{label}
							// 				</p>
							// 			</Space>
							// 		))}
							// 	</div>,
							// ]}
						>
							<Radio.Group
								value={selectedRoomId}
								onChange={(e) => {
									const room = JSON.parse(e.target.value);
									setSelectedRoomId(e.target.value);
									handleRoomChange(room);
								}}
								options={filteredRooms?.map((it) => ({
									label: <RoomCard key={it.id} {...it} />,
									value: JSON.stringify(it),
									disabled: it.count === it.maxiCount,
								}))}
								className="grid! grid-cols-3 gap-4! [&>.ant-radio-wrapper>.ant-radio]:hidden! [&>.ant-radio-wrapper>.ant-radio-label]:px-0!  [&>.ant-radio-wrapper>.ant-radio-label]:w-full! [&>.ant-radio-wrapper]:m-0! group"
							/>
						</Card>
					</>
				) : (
					<Empty
						description={"No rooms found!"}
						classNames={{
							root: "mt-2",
							description: "font-poppins",
						}}
					/>
				)}
			</Modal>
		</>
	);
};
